<!--
  Preloading strategy:
    - small spinner for breadcrumber
    - cascading fade in effect for the main content
-->
<template>
  <layout-teleport-to-breadcrumb>
    <layout-breadcrumb-loader
      :predicate="fetchState.status.value === 'success'"
    />
  </layout-teleport-to-breadcrumb>

  <!-- TAB: Calendar -->
  <section
    :key="NAV_SECTION.CALENDAR"
    :class="[
      'page-parameters__tab',
      'page-parameters__tab--calendar'
    ]"
  >
    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a2"
    >
      <div class="page-parameters__filters">

        <!-- Filter: Year -->
        <ui-buffered-container
          :is-pending="isPending"
          :idle-phase-duration="300"
          :sustain-phase-duration="300"
        >
          <template #default="{ onPreBufferPending, onBufferPending }">
            <ui-form-year-picker
              v-model="year"
              format="YYYY"
              :variant="UI.VARIANT.PRIMARY"
              :m-min="mYearMin"
              :m-max="mYearMax"
              class="page-parameters__filters__date"
              :status="onPreBufferPending ?
                UI.STATUS.MUTED :
                (
                  onBufferPending ?
                    UI.STATUS.DEFFERED :
                    UI.STATUS.ENABLED
                )
              "
              @update:model-value="() => updateData()"
            />
          </template>
        </ui-buffered-container>

      </div>
    </transition>

    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a1 d1"
    >
      <b-card
        :class="[
          'card--leveled',
          {
            'page-parameters--animate-busy': isPending
          }
        ]"
      >
        <ui-reset ref="resetContainer">
          <solidarity-day-form
            class="page-parameters__solidarity-block"
            v-bind="{
              mSolidarityDay,
              year,
              updateCallback: updateSolidarityDayCallback,
              deleteCallback: deleteSolidarityDayCallback,
            }"
          />

          <public-holiday-list
            v-bind="{
              items: publicHolidayList,
              updateCallback: updatePublicHolidayCallback,
            }"
          />
        </ui-reset>
      </b-card>
    </transition>

  </section>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

.page-parameters

  &__solidarity-block
    margin-bottom: $layout-spacer-y * .5

  ////////////////////////////////////////

  &__filters
    margin-bottom: $layout-spacer-y * .5

    /// quick & dirty UI on select
    ///   - prevent interaction (allowing only arrow CTA navigation)
    ///   - emphasise evolution: more turning into a navigation title rather
    ///     than an invitation to interaction
    &__date

      :deep(.ui-form-select)
        @include no-interaction
        background: mix-alpha($primary, 5%)
        padding-left: initial
        padding-right: initial
        font-size: 16px
        line-height: space-x(3.5)
        font-weight: $font-weight-bold
        color: $primary
        text-align: center
        border: initial

  ///////////////////////////////////////

  /// Deffered state strategy:
  ///   - simple pattern (same as a <ui-table> UI pending state handling)
  ///   - applying fade transition
  ///
  &--animate-busy
    @include no-interaction
    opacity: 0.6
    transition: 0.3s
    transition-delay: 0.3s

</style>

<script lang="ts">
import SolidarityDayForm from '~/components/transat/parameters/calendar/SolidarityDayForm.vue'
import PublicHolidayList from '~/components/transat/parameters/calendar/PublicHolidayList.vue'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useAccessControl } from '~/composables/layout/access-control'
import { useToastBuilder } from '~/composables/layout/toast'
import { NAV_SECTION } from '~/pages/data/[dataId]/conges/parameters.vue'
import Console from '~/utils/lib/core/console'
import { now } from '~/utils/lib/core/datetime'
import { __, equals, findIndex, Maybe, noop, propEq, tapCatch, toString, update } from '~/utils/lib/core/prelude'
import { useStore_transat_parameters_calendar, type PublicHoliday, type SolidarityDay } from '~/stores/transat/parameters/calendar'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import type { UiReset } from '#components'

const console = new Console('component:calendar')

export default defineNuxtComponent({
  components: { PublicHolidayList, SolidarityDayForm },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()
    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const setNavSection = inject<((_: NAV_SECTION) => void)>('setNavSection', noop)

    setNavSection(NAV_SECTION.CALENDAR)

    const ref_resetContainer
      = useTemplateRef<InstanceType<typeof UiReset>>('resetContainer')

    const store_parameters_calendar = useStore_transat_parameters_calendar()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ref_resetContainer,
      store_parameters_calendar,
    }
  },
  data() {
    const year = now({ outputFormat: 'YYYY' })

    return {
      NAV_SECTION,
      year,
      isPending: false,
      mYearMin: Maybe.Nothing<string>(),
      mYearMax: Maybe.Nothing<string>(),
      mSolidarityDay: Maybe.Nothing<SolidarityDay>(),
      publicHolidayList: [] as Array<PublicHoliday>,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.updateData()
    )
  },
  methods: {
    updateData() {
      const {
        structure: { uuidData },
        company: { code_societe },
      } = this.fromCompanyLevel()

      const options = {
        uuidData,
        code_societe,
        year: this.year
      }

      this.isPending = true

      return this.store_parameters_calendar.getDetails(options)
        .then(results => {
          this.isPending = false
          this.mYearMin = results.allow_previous_year ?
            Maybe.Nothing() :
            Maybe.Just(this.year)
          this.mYearMax = results.allow_next_year ?
            Maybe.Nothing() :
            Maybe.Just(this.year)
          this.mSolidarityDay = results.solidarity_day
          this.publicHolidayList = results.public_holidays

          this.ref_resetContainer?.reset()
        })
        .catch(tapCatch(err => {
          this.isPending = false

          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Récupération des données du calendrier`)
            .show()
        }))
    },
    updatePublicHolidayCallback(
      date :PublicHoliday["date"],
      type :PublicHoliday["type"]
    ) {
      const {
        structure: { uuidData },
        company: { code_societe }
      } = this.fromCompanyLevel()

      const options = {
        uuidData,
        code_societe,
        date,
        type
      }

      return this.store_parameters_calendar.updatePublicHolidayType(options)
        .then(res => {
          const idx = findIndex(
            propEq('date', res.date),
            this.publicHolidayList
          )

          if (equals(idx, -1))
            return

          this.publicHolidayList = update(idx, res, this.publicHolidayList)
        })
        .catch(tapCatch(err => {
          console.warn(err, 'updatePublicHoliday')
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification du jour férié`)
            .show()
        }))
    },
    updateSolidarityDayCallback(
      date :SolidarityDay,
    ) {
      const {
        structure: { uuidData },
        company: { code_societe }
      } = this.fromCompanyLevel()

      const year = this.year

      return this.store_parameters_calendar.updateSolidarityDay(
          { uuidData, code_societe, date }
        )
        .then(() => this.store_parameters_calendar.getDetails(
          { uuidData, code_societe, year }
        ))
        .then(results => {
          this.publicHolidayList = results.public_holidays
        })
        .catch(tapCatch(err => {
          console.warn(err, 'updateSolidarityDay')
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification du jour de solidarité`)
            .show()
        }))
    },
    deleteSolidarityDayCallback() {
      const {
        structure: { uuidData },
        company: { code_societe }
      } = this.fromCompanyLevel()

      const options = {
        uuidData,
        code_societe,
        year: this.year
      }

      return this.store_parameters_calendar.deleteSolidarityDay(options)
        .then(() => this.store_parameters_calendar.getDetails(options))
        .then(results => {
          this.publicHolidayList = results.public_holidays
        })
        .catch(tapCatch(err => {
          console.warn(err, 'deleteSolidarityDay')
          this.ToastBuilder
            .error(toString(err))
            .title(`Modification du jour de solidarité`)
            .show()
        }))
    },
  },
})
</script>
